import {
	Scene,
	Vector3,
	MeshBuilder,
	StandardMaterial,
	Color3,
	TransformNode,
} from "@babylonjs/core";
import { AdvancedDynamicTexture, Rectangle, TextBlock, Control } from "@babylonjs/gui";
import type { PresencePose } from "@utah-vr/shared";

interface Ghost {
	root: TransformNode;
	target: Vector3;
	targetRy: number;
	label: Rectangle;
}

const GHOST_COLORS = [
	new Color3(0.4, 0.7, 1.0),
	new Color3(1.0, 0.5, 0.6),
	new Color3(0.6, 1.0, 0.5),
	new Color3(1.0, 0.85, 0.3),
	new Color3(0.8, 0.5, 1.0),
];

function hashColor(id: string) {
	let h = 0;
	for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
	return GHOST_COLORS[Math.abs(h) % GHOST_COLORS.length]!;
}

export function createGhostManager(scene: Scene) {
	const adt = AdvancedDynamicTexture.CreateFullscreenUI("ghost-labels", true, scene);
	const ghosts = new Map<string, Ghost>();

	const spawn = (sessionId: string, pose: PresencePose) => {
		const root = new TransformNode(`ghost-${sessionId}`, scene);
		root.position.set(pose.x, pose.y, pose.z);

		const body = MeshBuilder.CreateCapsule(`ghost-body-${sessionId}`, { radius: 0.35, height: 1.8 }, scene);
		body.parent = root;
		body.position.y = 0.9;
		const mat = new StandardMaterial(`ghost-mat-${sessionId}`, scene);
		const color = hashColor(sessionId);
		mat.diffuseColor = color;
		mat.emissiveColor = color.scale(0.3);
		mat.alpha = 0.85;
		body.material = mat;

		const head = MeshBuilder.CreateSphere(`ghost-head-${sessionId}`, { diameter: 0.4 }, scene);
		head.parent = root;
		head.position.y = 1.95;
		head.material = mat;

		const labelAnchor = MeshBuilder.CreateBox(`ghost-anchor-${sessionId}`, { size: 0.05 }, scene);
		labelAnchor.parent = root;
		labelAnchor.position.y = 2.4;
		labelAnchor.isVisible = false;

		const rect = new Rectangle();
		rect.width = "120px";
		rect.height = "26px";
		rect.cornerRadius = 4;
		rect.thickness = 1;
		rect.color = "#fff";
		rect.background = "rgba(0,0,0,0.6)";
		adt.addControl(rect);
		rect.linkWithMesh(labelAnchor);
		rect.linkOffsetY = -8;
		const t = new TextBlock();
		t.text = `op-${sessionId.slice(0, 4)}`;
		t.color = "#fff";
		t.fontSize = 11;
		t.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
		rect.addControl(t);

		const ghost: Ghost = {
			root,
			target: new Vector3(pose.x, pose.y, pose.z),
			targetRy: pose.ry,
			label: rect,
		};
		ghosts.set(sessionId, ghost);
		return ghost;
	};

	const upsert = (sessionId: string, pose: PresencePose) => {
		const g = ghosts.get(sessionId) ?? spawn(sessionId, pose);
		g.target.set(pose.x, pose.y, pose.z);
		g.targetRy = pose.ry;
	};

	const remove = (sessionId: string) => {
		const g = ghosts.get(sessionId);
		if (!g) return;
		g.label.dispose();
		g.root.dispose();
		ghosts.delete(sessionId);
	};

	/** Lerp all ghosts toward their target pose. Call every frame. */
	const tick = (dt: number) => {
		const lerp = Math.min(1, dt * 6);
		for (const g of ghosts.values()) {
			g.root.position = Vector3.Lerp(g.root.position, g.target, lerp);
			const dr = ((g.targetRy - g.root.rotation.y + Math.PI) % (Math.PI * 2)) - Math.PI;
			g.root.rotation.y += dr * lerp;
		}
	};

	return { upsert, remove, tick };
}

export type GhostManager = ReturnType<typeof createGhostManager>;
